import React, { useState } from "react";
import {
    Box,
    Button,
    Paper,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
} from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import TollIcon from "@mui/icons-material/Toll";
import { useToken } from "../../items/UseToken";
import { useUser } from "../../../Hooks/UserContext";
import RegulationDetails from "./RegulationDetails";

export default function NumberSelection({ handleNext, setNumber }) {
    const [countryCode, setCountryCode] = useState("US");
    const [numberType, setNumberType] = useState(["mobile", "local", "toll free"]); // Default to all types
    const [availableNumbers, setAvailableNumbers] = useState([]);
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [endUserType, setEndUserType] = useState("business");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { token } = useToken();
    const { user } = useUser();

    const countryOptions = [
        { code: "US", name: "United States" },
        { code: "PL", name: "Poland" },
        { code: "DE", name: "Germany" },
        { code: "GB", name: "United Kingdom" },
        { code: "FR", name: "France" },
        { code: "ES", name: "Spain" },
        { code: "IT", name: "Italy" },
        { code: "AU", name: "Australia" },
        { code: "CA", name: "Canada" },
        { code: "JP", name: "Japan" },
        { code: "IN", name: "India" },
        { code: "BR", name: "Brazil" },
        { code: "MX", name: "Mexico" },
        { code: "NL", name: "Netherlands" },
        { code: "SE", name: "Sweden" },
        { code: "NO", name: "Norway" },
        { code: "FI", name: "Finland" },
        { code: "DK", name: "Denmark" },
        { code: "CH", name: "Switzerland" },
        { code: "BE", name: "Belgium" },
    ];

    const typeIcons = {
        mobile: <MobileFriendlyIcon color="primary" />,
        local: <LocalPhoneIcon color="secondary" />,
        "toll free": <TollIcon color="action" />,
    };

    const searchNumbers = async () => {
        setLoading(true);
        setError("");
        setAvailableNumbers([]);
        try {
            const results = await Promise.all(
                numberType.map(async (type) => {
                    try {
                        const response = await fetch(
                            `api/searchtwilionumbers?countryCode=${countryCode}&type=${type}`,
                            {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: "Bearer " + token,
                                },
                            }
                        );
                        if (!response.ok) throw new Error(`No numbers for ${type}`);
                        const data = await response.json();
                        return data.map((num) => ({ ...num, type })); // Add type to each number
                    } catch {
                        return []; // Return empty if fetch fails
                    }
                })
            );

            const mergedResults = results.flat();
            if (mergedResults.length === 0) {
                setError("No phone numbers available for the selected criteria.");
            }
            setAvailableNumbers(mergedResults);
        } catch (err) {
            setError("Failed to fetch phone numbers. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const buyNumber = async () => {
        if (!selectedNumber) return;
        setLoading(true);
        setError("");
        try {
            const response = await fetch(`api/buytwilionumber`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({ phoneNumber: selectedNumber, registered_by: user.username }),
            });
            if (!response.ok) {
                let errorMsg = await response.text();
                throw new Error(" \n\n"+errorMsg);
            }
            handleNext();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCountryChange = (event) => {
        setCountryCode(event.target.value);
        setAvailableNumbers([]);
        setError("");
        setSelectedNumber(null);
    };

    return (
        <Box sx={{  mt: 4 }}>
            <Typography variant="h6" textAlign="center">
                Search and Purchase Phone Numbers
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
                <FormControl sx={{ width: 200 }}>
                    <InputLabel>Country</InputLabel>
                    <Select
                        value={countryCode}
                        onChange={handleCountryChange}
                        label="Country"
                    >
                        {countryOptions.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ width: 200 }}>
                    <InputLabel>Number Types</InputLabel>
                    <Select
                        multiple
                        value={numberType}
                        onChange={(e) => setNumberType(e.target.value)}
                        label="Number Types"
                        renderValue={(selected) => selected.join(", ")}
                    >
                        <MenuItem value="mobile">Mobile</MenuItem>
                        <MenuItem value="local">Local</MenuItem>
                        <MenuItem value="toll free">Toll Free</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{ width: 200 }}>
                    <InputLabel>Register for</InputLabel>
                    <Select
                        
                        value={endUserType}
                        onChange={(e) => setEndUserType(e.target.value)}
                        label="Number Types"
                    >
                        <MenuItem value="business">Company</MenuItem>
                        <MenuItem value="individual">Individual</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
            <Button
                variant="contained"
                onClick={searchNumbers}
                sx={{ mt: 2 }}
                disabled={loading}
            >
                Search Numbers
                </Button>
            </Box>
            {loading &&
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
                    <CircularProgress sx={{ mt: 2 }} />
                </Box>}
            {error && error.includes("Twilio") ?
                (<Paper sx={{ padding: 2 }}>
                    <Typography color="black" sx={{ mt: 2 }}>
                    <p>Buying a number in this country requires additional regulatory compliance, it is required by the local law, please got to your dashboard to complete regulatory compliance.</p>
                    </Typography>
                    <a href="https://sophona.com/dashboard/telephony">Go to Telephony Dashboard</a>
                <Typography color="darkred" sx={{ mt: 2 }}>
                   Details:  {error} 
                    </Typography>
                </Paper>
                ) :
                (
                    <Typography color="error" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                ) }
            {availableNumbers.length > 0 && (
                <>
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6">Available Numbers</Typography>
                    {availableNumbers.map((num) => (
                        <Paper
                            key={num.phone_number}
                            sx={{
                                padding: 2,
                                margin: 1,
                                cursor: "pointer",
                                backgroundColor:
                                    selectedNumber === num.phone_number ? "#1976d2" : "white",
                                color: selectedNumber === num.phone_number ? "white" : "black",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                            }}
                            onClick={() => {
                                setSelectedNumber(num.phone_number);
                                setNumber(num.phone_number);
                            }
                            }
                        >
                            <Tooltip title={`Type: ${num.type}`}>
                                {typeIcons[num.type]}
                            </Tooltip>
                            <Box>
                                <Typography variant="body1">
                                    <strong>{num.friendly_name}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Voice: {num.capabilities.voice ? "Yes" : "No"}, SMS:{" "}
                                    {num.capabilities.sms ? "Yes" : "No"}, MMS:{" "}
                                    {num.capabilities.mms ? "Yes" : "No"}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                ${num.pricePerMonth}/month
                            </Typography>
                            <RegulationDetails countryCode={countryCode} numberType={num.type} endUserType={endUserType} />

                        </Paper>
                    ))}
                </Box>
                <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1">Legend:</Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}>
                    <Tooltip title="Local">
                        <LocalPhoneIcon color="secondary" />
                    </Tooltip>
                    <Typography>Local</Typography>
                    <Tooltip title="Mobile">
                        <MobileFriendlyIcon color="primary" />
                    </Tooltip>
                    <Typography>Mobile</Typography>
                    <Tooltip title="Toll Free">
                        <TollIcon color="action" />
                    </Tooltip>
                    <Typography>Toll Free</Typography>
                </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={buyNumber}
                        disabled={!selectedNumber || loading}
                        sx={{ mt: 2 }}
                    >
                        Buy Selected Number
                        </Button>
                    </Box>
                </>

            )}
            
            
        </Box>
    );
}
